import type { FeatureContent } from './types';

export const applicationTracking: FeatureContent = {
  slug: 'application-tracking',
  path: '/features/application-tracking',
  audience: 'candidate',
  icon: 'KanbanSquare',
  name: 'Application Tracking',
  navLabel: 'Application Tracking',
  navDescription: 'Always know where you stand. No more black hole.',
  primaryKeyword: 'job application tracking',
  seo: {
    title: 'Job Application Tracking, Real-Time Status | Talenture.ai',
    description:
      'Job application tracking with honest, real-time status from the recruiter side. See when you are viewed, shortlisted, or interviewing. No more black hole.',
    keywords: [
      'job application tracking',
      'real-time application status',
      'application tracker',
      'no ghosting',
      'recruiter status updates',
      'application status tracking',
    ],
    ogTitle: 'Always Know Where You Stand | Talenture.ai',
    ogDescription:
      'Real-time, honest application status from the recruiter side. See when you are viewed, shortlisted, or interviewing. No more black hole.',
  },
  hero: {
    eyebrow: 'For candidates',
    h1: 'Job Application Tracking: Always Know Where You Stand',
    subhead:
      'You apply and then hear nothing. Talenture replaces the silence with real, recruiter-side status, so you can see when your application is viewed, shortlisted, or moving to interview.',
    primaryCta: { label: 'Browse jobs', href: '/jobs' },
    secondaryCta: { label: 'Create free account', href: '/candidate-auth' },
  },
  definition: {
    heading: 'What is real-time application tracking?',
    body: 'Real-time application tracking shows the true status of your application as it changes on the recruiter side, not a status you logged yourself. Talenture shows each stage, from applied and viewed through interview requested and planned to offer, and updates live, so you never have to wonder whether anyone has seen your application.',
  },
  sections: [
    {
      icon: 'Columns3',
      title: 'Every stage, visible',
      body: 'Your application moves through clear stages, and you see each one as it happens, so the process stops being a mystery.',
      bullets: [
        'Applied',
        'Viewed',
        'Interview requested',
        'Interview planned',
        'Offer',
      ],
    },
    {
      icon: 'Eye',
      title: 'Real recruiter-side truth',
      body: 'This is not a status you set in a spreadsheet. The viewed signal comes from the recruiter actually opening your application, so what you see is what is really happening.',
    },
    {
      icon: 'RefreshCw',
      title: 'Updates that keep up',
      body: 'Status updates live, even for long analyses, and survives closing your tab. You come back to the current truth, not a stale snapshot.',
    },
    {
      icon: 'MessageSquareText',
      title: 'A no still comes with a reason',
      body: 'When an application does not progress, it pairs with personalised feedback, so even a rejection tells you something useful instead of leaving you guessing.',
    },
  ],
  howTo: {
    heading: 'How application tracking works',
    intro: 'Three steps from application to an honest next update.',
    steps: [
      {
        title: 'Apply and enter the live pipeline',
        body: 'Your application starts at Applied and moves through clear stages such as Viewed, Interview requested, Interview planned, and Offer.',
      },
      {
        title: 'See recruiter-side updates',
        body: 'The status changes when the recruiter actually views or advances your application, and the live state remains current even after you close your tab.',
      },
      {
        title: 'Get a reason when it stops',
        body: 'If the application does not progress, personalised feedback explains the skill-based gaps instead of leaving you with silence.',
      },
    ],
  },
  proofPoints: [
    { value: '5 stages', label: 'tracked end to end' },
    { value: 'Real-time', label: 'recruiter-side status' },
    { value: 'Always free', label: 'for candidates' },
  ],
  faq: [
    {
      question: 'How is this different from a spreadsheet or tracker app?',
      answer:
        'A tracker app only shows what you type into it. Talenture shows the real status from the recruiter side, including when your application is actually viewed, so the information is the truth of the process, not your own best guess.',
    },
    {
      question: 'Will I know when a recruiter has seen my application?',
      answer:
        'Yes. A viewed signal appears when the recruiter opens your application, so the silence after applying is replaced with a real indication that a human has looked at it.',
    },
    {
      question: 'Does the status update in real time?',
      answer:
        'It does. Status updates live and survives closing your tab, even for longer analyses, so when you return you see the current state rather than an outdated snapshot.',
    },
    {
      question: 'What happens if I am not selected?',
      answer:
        'Application tracking pairs with personalised feedback, so a no still comes with a reason. Instead of being ghosted, you get specific, skill-based feedback on where you fell short.',
    },
  ],
  relatedSlugs: ['match-insights', 'upskilling-recommendations', 'candidate-feedback'],
};
